import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Settings } from "./Settings";
import { Symbol } from "../GameComponents/Symbol";
import { Machine } from "../GameComponents/Machine";
import { Tweens } from "../Components/Effects/Tweens";
import { BaseView } from "../GameComponents/Base/BaseView";

/**
 * Class reel represent reel symbols rotationExtension of original Reel class.
 * @extends ReelExtended
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    public rollDirection: number = 1;
    protected isForceStopped: boolean = false;

    private _symbolCaseInUpdateReel = Symbol.CaseBlur;

    public get symbolCaseInUpdateReel(): string {
        return this._symbolCaseInUpdateReel;
    }

    public set symbolCaseInUpdateReel(value: string) {
        this._symbolCaseInUpdateReel = value;
    }

    //******************************************************************************************************************
    //  SPIN START
    //******************************************************************************************************************

    /**
     *
     */
    public spinStart(): void {
        this.isForceStopped = false;

        super.spinStart();
    }

    //******************************************************************************************************************
    //  SPIN STOP
    //******************************************************************************************************************

    /**
     * @param {boolean} force
     * @param {number} customDelay
     */
    public spinStop(force: boolean = false, customDelay: number = 0): void {
        this.isForceStopped = force;

        if (force) {
            this.isTeasing = false;
            this.teasingSymbols = [];
        }

        super.spinStop(force, customDelay);
    }

    //******************************************************************************************************************
    //  SPIN PRE ROLL
    //******************************************************************************************************************

    protected spinPreRoll(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY - this.preRoll * this.rollDirection, this.positionY],
            tweenDuration: this.preRollSpeed * 2,
            tweenDelay: this.startDelay,
            callbackFunction: this.spinRoll,
            callbackScope: this,
        });
    }

    //******************************************************************************************************************
    //  SPIN ROLL
    //******************************************************************************************************************

    protected spinRoll(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.symbolHeight * this.rollDirection],
            tweenDuration: this.rollSpeed,
            callbackFunction: this.updateReel,
            callbackScope: this,
        });
    }

    protected updateReel(): void {
        if (this.rollDirection > 0) {
            this.returnSymbol(this.currentSymbols.pop());
        } else {
            this.returnSymbol(this.currentSymbols.shift());
        }

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if ((<ReelExtended>this.previousReel).isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = this.symbolCaseInUpdateReel;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                if (this.rollDirection > 0) {
                    symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                } else {
                    symbolId = this.data.symbols[this.stoppingCounter - 1];
                }

                symbolCase = Symbol.CaseRotation;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
            }

            this.stoppingCounter++;
        }

        if (symbolId == Settings.SymbolBonus) {
            // Every reel has different color of bonus symbol
            symbolId += this.index;

            if (this.isTeasing && symbolCase == Symbol.CaseBlur) {
                symbolCase = Symbol.CaseBlur + "_TEASE";
            }
        }

        if (this.rollDirection > 0) {
            this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));

            this.addChild(this.currentSymbols[0]);
        } else {
            let lenght = this.currentSymbols.push(this.getSymbol(symbolId - 1, symbolCase));

            this.addChild(this.currentSymbols[lenght - 1]);
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    //******************************************************************************************************************
    //  SPIN POST ROLL
    //******************************************************************************************************************

    protected spinPostRoll(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.postRoll * this.rollDirection, this.positionY],
            tweenDuration: this.postRollSpeed * 2,
            callbackFunction: this.spinComplete,
            callbackScope: this,
        });
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected getSymbol(symbolId: number, symbolCase: string): Symbol {
        const symbol = super.getSymbol(symbolId, symbolCase);

        if (this.isTeasing) {
            if (this.teasingSymbols.indexOf(symbol.id) !== -1) {
                // Teased symbol
                if (symbol.getCurrentCase() === Symbol.CaseBlur) {
                    symbol.goToCase(Symbol.CaseBlur);
                }
            } else {
                // Non-teased symbol
                if (symbol.getCurrentCase() === Symbol.CaseBlur || symbol.getCurrentCase() === Symbol.CaseRotation) {
                    symbol.goToCase(Symbol.CaseNowin); //Symbol.CaseNowinBlur
                }
            }
        } else {
            if (BaseView.PossibleSymbolsOnReel != null && BaseView.PossibleSymbolsOnReel[this.index] != null) {
                if (
                    BaseView.PossibleSymbolsOnReel[this.index].length == 0 ||
                    (BaseView.PossibleSymbolsOnReel[this.index].length > 0 &&
                        BaseView.PossibleSymbolsOnReel[this.index].indexOf(symbol.id) === -1)
                ) {
                    symbol.goToCase("IMPOSSIBLE_WIN");
                } else {
                    symbol.goToCase(symbolCase);
                }
            } else {
                symbol.goToCase("IMPOSSIBLE_WIN");
            }
        }

        return symbol;
    }

    protected returnSymbol(symbol: Symbol): void {
        symbol.alpha = 1;
        this.symbols.returnSymbol(symbol);
    }

    public setTease(isTeasing: boolean, teasingSymbols: Array<number> = null): void {
        this.isTeasing = isTeasing;
        this.teasingSymbols = teasingSymbols;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/